import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getArray } from '../../../../utils/typeUtils';
import { ROW_ANIMATION_DURATION_MS, MAX_WAVE_DELAY_MS } from '../constants/animation.constants';
import {
  animateRowExits,
  measureRowPositions,
  playRowTransitions,
  prepareRowTransitions,
  resetRowStyles,
} from '../utils/animation.utils';

export default function useAnimatedQueueRows({ rows, dataSrcId, enabled, isLoading }) {
  const [renderedRows, setRenderedRows] = useState(() => getArray(rows));
  const tableRef = useRef(null);
  const rowElementsRef = useRef(new Map());
  const displayedRowsRef = useRef(getArray(rows));
  const pendingLayoutAnimationRef = useRef(null);
  const exitPendingRef = useRef(false);
  const previousSourceRef = useRef(dataSrcId);

  const registerRow = useCallback((uuid, element) => {
    if (element) {
      rowElementsRef.current.set(uuid, element);
    } else {
      rowElementsRef.current.delete(uuid);
    }
  }, []);

  useLayoutEffect(() => {
    const nextRows = getArray(rows);
    const previousRows = displayedRowsRef.current;
    const sourceChanged = previousSourceRef.current !== dataSrcId;
    const exitInterrupted = exitPendingRef.current;
    previousSourceRef.current = dataSrcId;
    exitPendingRef.current = false;

    const elements = rowElementsRef.current;
    const wrapper = tableRef.current?.closest('.custom-table__wrapper');
    const originalOverflowX = wrapper?.style.overflowX;
    const pendingFrames = new Set();
    let timeoutId;
    let disposed = false;

    const scheduleFrame = callback => {
      const frameId = requestAnimationFrame(() => {
        pendingFrames.delete(frameId);

        if (!disposed && !document.hidden) {
          callback();
        }
      });

      pendingFrames.add(frameId);
    };

    const resetAnimation = () => {
      clearTimeout(timeoutId);
      pendingFrames.forEach(cancelAnimationFrame);
      pendingFrames.clear();
      pendingLayoutAnimationRef.current = null;
      resetRowStyles(elements);

      if (wrapper) {
        wrapper.style.overflowX = originalOverflowX;
      }
    };

    const commitRows = () => {
      exitPendingRef.current = false;
      displayedRowsRef.current = nextRows;
      setRenderedRows(nextRows);
    };

    const showLatestRows = () => {
      resetAnimation();
      commitRows();
    };

    const structureChanged =
      previousRows.length !== nextRows.length ||
      previousRows.some((row, index) => row.uuid !== nextRows[index]?.uuid);
    
    const shouldAnimate =
      enabled && !isLoading && !document.hidden && !sourceChanged && !exitInterrupted && structureChanged;

    const animateUpdate = () => {
      const previousIds = new Set(previousRows.map(row => row.uuid));
      const nextIds = new Set(nextRows.map(row => row.uuid));
      const removedRows = previousRows.filter(row => !nextIds.has(row.uuid));
      const firstRemovedIndex = previousRows.findIndex(row => !nextIds.has(row.uuid));
      const previousPositions = measureRowPositions(elements);
      
      if (wrapper && wrapper.scrollWidth <= wrapper.clientWidth) {
        wrapper.style.overflowX = 'hidden';
      }

      const animateLayout = () => {
        const transitions = prepareRowTransitions({
          rows: nextRows,
          elements,
          previousIds,
          previousPositions,
          firstRemovedIndex,
        });
        
        // Paint the starting positions before transitioning to the new layout.
        scheduleFrame(() =>
          scheduleFrame(() => {
            playRowTransitions(transitions);
            timeoutId = setTimeout(resetAnimation, ROW_ANIMATION_DURATION_MS + MAX_WAVE_DELAY_MS);
          })
        );
      };

      const commitAnimatedRows = () => {
        if (disposed) {
          return;
        }
        
        pendingLayoutAnimationRef.current = animateLayout;
        commitRows();
      };

      if (removedRows.length === 0) {
        commitAnimatedRows();
        return;
      }

      exitPendingRef.current = true;
      animateRowExits(removedRows, elements);
      timeoutId = setTimeout(commitAnimatedRows, ROW_ANIMATION_DURATION_MS);
    };

    // Never restart an interrupted exit: frequent updates could otherwise
    // postpone removal indefinitely. Source and visibility changes also snap.
    if (shouldAnimate) {
      animateUpdate();
    } else {
      showLatestRows();
    }

    document.addEventListener('visibilitychange', showLatestRows);
    
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', showLatestRows);
      resetAnimation();
    };
  }, [rows, dataSrcId, enabled, isLoading]);

  useLayoutEffect(() => {
    // React must commit the new rows before we can measure their destinations.
    const animateLayout = pendingLayoutAnimationRef.current;
    pendingLayoutAnimationRef.current = null;
    animateLayout?.();
  }, [renderedRows]);

  return { tableRef, registerRow, displayedRows: enabled ? renderedRows : getArray(rows) };
}
